import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardBody, Button, Alert } from '@components/ui';
import { Logo } from '@components/common/Logo';

/**
 * Página de aterrizaje del enlace "Cambiar contraseña" que envía el correo de
 * recuperación (llega como /reset-password?email=...).
 *
 * Antes resolvía el `userId` de la cuenta llamando a /auth/get-user-by-email,
 * un endpoint público que devolvía además el `PasswordHash` y el
 * `SecurityStamp` del usuario (FL10-CRITICAL-008). Ese endpoint fue retirado.
 *
 * El flujo vive ahora en /forgot-password, donde el BFF resuelve la cuenta del
 * lado del servidor y entrega un token opaco. Como ese token se emite al pedir
 * el código y no se puede recuperar a partir del correo, esta página redirige
 * allí con el email precargado: el usuario pide un código nuevo y continúa.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  useEffect(() => {
    const target = email
      ? `/forgot-password?email=${encodeURIComponent(email)}`
      : '/forgot-password';
    const timer = setTimeout(() => navigate(target, { replace: true }), 2500);
    return () => clearTimeout(timer);
  }, [email, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-100 dark:bg-secondary-900 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo height={80} />
          </div>
          <p className="text-secondary-600 dark:text-secondary-400">
            Portal de Facturación Electrónica
          </p>
        </div>

        <Card>
          <CardBody className="p-8">
            <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">
              Recuperar Contraseña
            </h2>

            <Alert variant="info" title="Te estamos redirigiendo">
              Continúa el proceso desde la pantalla de recuperación. Vas a poder pedir un
              código nuevo y crear tu contraseña ahí mismo.
            </Alert>

            <Link to={email ? `/forgot-password?email=${encodeURIComponent(email)}` : '/forgot-password'}>
              <Button variant="primary" className="w-full mt-6">
                Continuar
              </Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
